class AjaxCartItem extends HTMLElement {
  constructor() {
    super();

    window.addEventListener("message", (event) => {
      if (event.data.type === "updateCartItems") {
        this.updateItemElement(event.data.cart);
      }
    });
  }

  async connectedCallback() {
    this.line = JSON.parse(/** @type {string} **/ (this.dataset.line));
    const data = JSON.parse(/** @type {string} **/ (this.dataset.item));
    this.tierDiscount = this.dataset.tierDiscount;
    this.showShipsBy = this.dataset.showShipsBy === "true" ? true : false;

    /**
     * @typedef {Object} Item
     * @property {number} id - The unique identifier for the item.
     * @property {string} url - The URL of the item.
     * @property {string} image - The URL of the item's image with a size suffix.
     * @property {string} title - The title of the product.
     * @property {string} vendor - The vendor of the product.
     * @property {string} originalPrice - The original price of the product formatted as a dollar currency string.
     * @property {string} finalPrice - The final price of the product formatted as a dollar currency string.
     * @property {string} [strikethroughPrice] - The original price formatted as a dollar currency string if it differs from the final price, otherwise an empty string.
     * @property {number} quantity - The quantity of the product.
     * @property {number} productId - The unique identifier for the product.
     * @property {?number} [brandLimit] - The brand limit for the product, if any.
     * @property {?number} [productLimit] - The product limit for the product, if any.
     * @property {?string} [limitType] - The type of limit for the product, if any.
     * @property {boolean} [hideQuantitySelector] - Whether to hide the quantity selector for the product.
     * @property {boolean} [isVoucherProduct] - Whether the product is a voucher product.
     * @property {string} [shipsBy] - The relative shipping date for the product.
     * @property {boolean} [isCustomSkuProduct] - Whether the product is a custom sku product.
     */

    /**
     * @type {Item}
     * */
    this.item = {
      id: data.id,
      url: data.url,
      image: data.image ? data.image.replace(/(\.[^.]*)$/, "_180x$1") : "",
      title: data.product_title,
      vendor: data.vendor,
      originalPrice: knocking.formatToDollarCurrencyString(data.original_price),
      finalPrice: knocking.formatToDollarCurrencyString(data.final_price),
      strikethroughPrice:
        data.final_price !== data.original_price
          ? knocking.formatToDollarCurrencyString(data.original_price)
          : "",
      quantity: data.quantity,
      productId: data.product_id,
      brandLimit: null,
      productLimit: null,
      hideQuantitySelector: data.properties["_hideQtySelector"] || false,
      isVoucherProduct: data.properties["_encoded-vouchers"] || false,
      shipsBy: data.properties["_ships-by"] || null,
      isCustomSkuProduct: data.properties["_custom-sku-variant"] || false,
    };
    this.item.brandLimit = knocking.getBrandLimit(this.item.vendor);
    this.item.productLimit = knocking.getProductLimit(
      this.item.productId.toString()
    );
    if (
      !this.item.productLimit &&
      !this.item.brandLimit &&
      window.enableGlobalProductLimit
    ) {
      this.item.productLimit = window.globalProductLimit;
    }

    this.item.limitType = (() => {
      if (this.item.productLimit) {
        return "product";
      } else if (this.item.brandLimit) {
        return "brand";
      } else {
        return null;
      }
    })();
    // console.log("item: ", this.item);
    let itemMeta = this.getItemMeta();
    let qtySelectorTemplate = `
      <quantity-input-cart class="quantity" data-product-id="${
        this.item.productId
      }" data-vendor="${
      this.item.vendor
    }" data-is-cart-item="true" data-line="${this.line}">
        ${
          this.item.isCustomSkuProduct
            ? ""
            : `
        <button class="quantity__button no-js-hidden" name="minus" type="button" tabindex="0">
          <span class="visually-hidden">Decrease quantity</span>
          <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-minus" fill="none" viewBox="0 0 10 2">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 110 1H1A.5.5 0 01.5 1z" fill="currentColor">
          </svg>
        </button>`
        }
        <input class="quantity__input"
            tabindex="-1"
            type="number"
            step="1"
            name="quantity"
            id="quantity-${this.line}"
            min="0"
            value="${this.item.quantity}"
            form="ajax-cart-form"
            readonly
          >
        ${
          this.item.isCustomSkuProduct
            ? ""
            : `
        <button class="quantity__button no-js-hidden" name="plus" type="button">
          <span class="visually-hidden">Increase quantity</span>
          <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-plus" fill="none" viewBox="0 0 10 10">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M1 4.51a.5.5 0 000 1h3.5l.01 3.5a.5.5 0 001-.01V5.5l3.5-.01a.5.5 0 00-.01-1H5.5L5.49.99a.5.5 0 00-1 .01v3.5l-3.5.01H1z" fill="currentColor">
          </svg>
        </button>
        `
        }
      </quantity-input-cart>
      `;

    let template = `
    <div class="ajaxcart__row">
      <div class="ajaxcart__product-image">
        <a href="${
          this.item.isCustomSkuProduct ? "#" : this.item.url
        }" tabindex="-1">
          <img src="${this.item.image}" alt="Image of ${this.item.title}">
        </a>
        <div class="ships-by-wrapper" aria-live="polite"></div>
      </div>
      <div class="ajaxcart__product-content">
        <div class="ajaxcart__product-name--wrapper" id="ajaxcart-item-name-wrapper">
          <a href="${
            this.item.isCustomSkuProduct ? "#" : this.item.url
          }" class="ajaxcart__product-name">
            <div class="vendor">${this.item.vendor}</div> ${this.item.title}
          </a>
        </div>
        <div class="ajaxcart__product-add">
          ${this.item.hideQuantitySelector ? "" : qtySelectorTemplate}
          <div class="ajaxcart__price_wrapper">
            <div class="ajaxcart__strikethrough"><strike>${
              this.item.strikethroughPrice
            }</strike></div>
            <div class="ajaxcart__price">${this.item.finalPrice}</div>
          </div>
        </div>
        ${
          this.item.isCustomSkuProduct
            ? ""
            : `
        <button type="button" class="ajaxcart__product-remove" id="remove-item-button">
          <svg xmlns="http://www.w3.org/2000/svg" width="10.46" height="12.879" viewBox="0 0 10.46 12.879" class="icon icon-remove">
            <g id="delete" transform="translate(0.003 0.001)">
              <path id="Path_230" data-name="Path 230" d="M222.7,154.7a.3.3,0,0,0-.3.3v5.7a.3.3,0,0,0,.6,0V155A.3.3,0,0,0,222.7,154.7Zm0,0" transform="translate(-215.694 -150.038)" fill="#666"/>
              <path id="Path_231" data-name="Path 231" d="M104.7,154.7a.3.3,0,0,0-.3.3v5.7a.3.3,0,0,0,.6,0V155A.3.3,0,0,0,104.7,154.7Zm0,0" transform="translate(-101.253 -150.038)" fill="#666"/>
              <path id="Path_232" data-name="Path 232" d="M.853,3.833v7.431A1.664,1.664,0,0,0,1.3,12.412a1.485,1.485,0,0,0,1.078.466H8.08a1.485,1.485,0,0,0,1.078-.466A1.664,1.664,0,0,0,9.6,11.264V3.833a1.152,1.152,0,0,0-.3-2.266H7.761V1.19A1.185,1.185,0,0,0,6.566,0H3.888A1.185,1.185,0,0,0,2.693,1.19v.377H1.149a1.152,1.152,0,0,0-.3,2.266ZM8.08,12.275H2.374a.955.955,0,0,1-.917-1.01v-7.4H9v7.4A.955.955,0,0,1,8.08,12.275ZM3.3,1.19A.581.581,0,0,1,3.888.6H6.566a.581.581,0,0,1,.591.588v.377H3.3Zm-2.147.98H9.3a.543.543,0,0,1,0,1.086H1.149a.543.543,0,1,1,0-1.086Zm0,0" transform="translate(0 0)" fill="#666"/>
              <path id="Path_233" data-name="Path 233" d="M163.7,154.7a.3.3,0,0,0-.3.3v5.7a.3.3,0,0,0,.6,0V155A.3.3,0,0,0,163.7,154.7Zm0,0" transform="translate(-158.473 -150.038)" fill="#666"/>
            </g>
          </svg>
          Remove
        </button>
        `
        }
      </div>
      <div class="tier-discount-message" style="display: none;"><strong>Product Quantity Discount</strong>  |  <span class="tier-discount-percent-output"></span>% Applied</div>
      <!-- Add this later
      <div class="ajaxcart__product-upsell">
        <div class="ajaxcart__product-image">
            <img src="https://placehold.co/200x200" alt="Product title">
        </div>
        <div class="ajaxcart__product-content">
          <div>
            <div class="heading">Want to upgrade?</div>
            <div class="product-title">Product title here</div>
          </div>
          <div class="ajaxcart__product-add">
              <div class="range">
              <span class="ajaxcart__price">$60.00</span>
              <s class="ajaxcart__price">$150.00</s>
              </div>
              <a href="#" class="button">Yes, Add it</a>
          </div>
        </div>
      </div>
      -->
    </div>

    <div class="limit_alert limit_alert_hide" style="" aria-hidden="true">
      <div class="limit_alert_notice">
        <div class="limit-icon-need-info">
          <span>i</span>
        </div>
        <span>You've reached the purchase limit for this ${
          this.item.limitType
        }</span>
        <button type="button" class="limit_info_cart__close-button" aria-label="close cart" tabindex="-1">
          <svg  aria-hidden="true" focusable="false" role="presentation" width="12" height="13" class="icon icon-close-small" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8.48627 9.32917L2.82849 3.67098" stroke="#333030" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M2.88539 9.38504L8.42932 3.61524" stroke="#333030" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    </div>

    <div class="sold_out_cart_alert" style="" aria-hidden="true">
      <div class="sold_out_cart_alert_notice">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" aria-hidden="true" focusable="false" role="presentation" class="sold_out_icon icon-unavailable viewBox="0 0 20 20">
          <path fill="#DE3618" stroke="#fff" d="M13.94 3.94L10 7.878l-3.94-3.94A1.499 1.499 0 103.94 6.06L7.88 10l-3.94 3.94a1.499 1.499 0 102.12 2.12L10 12.12l3.94 3.94a1.497 1.497 0 002.12 0 1.499 1.499 0 000-2.12L12.122 10l3.94-3.94a1.499 1.499 0 10-2.121-2.12z"></path>
        </svg>
        <span>All available items are already in your cart</span>
      </div>
    </div>
    `;
    this.render(template, itemMeta);

    /**
     * @type {NodeListOf<HTMLButtonElement> | null}
     * */
    const qtyButtons = this.querySelectorAll(".quantity__button");
    if (qtyButtons) {
      qtyButtons.forEach((button) => {
        button.addEventListener("click", () => {
          // console.log("qtyButton click event");
          this.enableLimitCheck();
        });
      });
    }

    if (!this.isLimitCheckDisabled) {
      this.checkLimits();
    }

    this.checkLimitLock = false;

    messenger.listen(
      messenger.topics.CART_READY,
      () => {
        if (this.isLimitCheckDisabled && this.isLimitCheckDisabled()) {
          return;
        }
        this.checkLimits();
      },
      "AjaxCartItem"
    );

    messenger.listen(
      messenger.topics.CHECK_LIMITS,
      (cart) => {
        if (this.isLimitCheckDisabled && this.isLimitCheckDisabled()) {
          return;
        }
        this.checkLimits(cart);
      },
      "AjaxCartItem"
    );

    messenger.listen(
      messenger.topics.UPDATE_CART_ITEMS,
      (cart) => {
        this.updateItemElement(cart);
      },
      "AjaxCartItem"
    );

    messenger.listen(messenger.topics.LINE_ITEM_ERROR, (data) => {
      // console.log(data.line + "---" + this.line);
      if (data.line === this.line) {
        console.log("line item error: ", data);
        this.showSoldOutAlert();
      }
    });

    const removeButton = this.querySelector("#remove-item-button");
    if (removeButton) {
      removeButton.addEventListener(
        "click",
        store.removeItem.bind(this, this.line)
      );
    }

    const limitInfoCloseButton = this.querySelector(
      ".limit_info_cart__close-button"
    );
    if (limitInfoCloseButton) {
      limitInfoCloseButton.addEventListener("click", () => {
        this.disableLimitCheck();
        this.hideLimitInfo();
      });
    }

    const cart = await store.getCart();
    if (cart) {
      this.updateItemElement(cart);
    }
    this.hideSoldOutAlert();

    const minusButton = this.querySelector('button[name="minus"]');
    if (minusButton) {
      minusButton.addEventListener("click", () => {
        this.hideLimitInfo();
      });
    }
  }

  disableLimitCheck() {
    // console.log("disableLimitCheck");
    if (!this.item) {
      console.error("Item not found");
      return;
    }
    // this.limitCheckDisabled = true;
    sessionStorage.setItem(
      `${this.item.productId}-limit_check_disabled`,
      "true"
    );
  }

  enableLimitCheck() {
    // console.log("enableLimitCheck");
    if (!this.item) {
      console.error("Item not found");
      return;
    }
    // this.limitCheckDisabled = false;
    sessionStorage.setItem(
      `${this.item.productId}-limit_check_disabled`,
      "false"
    );
  }

  /**
   * @returns {boolean}
   */
  isLimitCheckDisabled() {
    if (!this.item) {
      console.error("Item not found, returning false");
      return false;
    }
    const limitCheckDisabled = sessionStorage.getItem(
      `${this.item.productId}-limit_check_disabled`
    );
    // console.log("limitCheckDisabled: ", limitCheckDisabled);
    if (!limitCheckDisabled) {
      return false;
    }

    return limitCheckDisabled === "true" ? true : false;
  }

  /**
   * @param {Cart | null} cart
   */
  async checkLimits(cart = null) {
    if (!this.item) {
      console.error("Item not found, returning");
      return;
    }
    if (!cart) {
      cart = await store.getCart();
    }
    const limitStatus = await knocking.returnLimitStatus(
      cart,
      0,
      this.item.productId.toString(),
      this.item.vendor
    );
    if (
      limitStatus &&
      limitStatus.limitReached &&
      !this.item.hideQuantitySelector
    ) {
      // this.showLimitInfo();
      this.attachLimitNoticeListener();
    } else {
      // this.hideLimitInfo();
      this.detachLimitNoticeListener();
    }
  }

  attachLimitNoticeListener() {
    const plusButton = this.querySelector('button[name="plus"]');
    if (!plusButton) {
      console.error("Plus button not found");
      return;
    }
    // Store the bound function so we can remove it later
    if (!this.boundShowLimitInfo) {
      this.boundShowLimitInfo = this.showLimitInfo.bind(this);
    }
    plusButton.addEventListener("click", this.boundShowLimitInfo);
  }

  detachLimitNoticeListener() {
    const plusButton = this.querySelector('button[name="plus"]');
    if (!plusButton) {
      console.error("Plus button not found");
      return;
    }
    // Use the stored bound function reference
    if (this.boundShowLimitInfo) {
      plusButton.removeEventListener("click", this.boundShowLimitInfo);
    }
  }

  hideLimitInfo() {
    const limitInfoElement = this.querySelector(".limit_alert");
    if (!limitInfoElement) {
      // console.log("Cannot find limit-info element! Might not be rendered yet...");
      return;
    }
    limitInfoElement.classList.remove("limit_alert_show");
    limitInfoElement.classList.add("limit_alert_hide");
    limitInfoElement.setAttribute("aria-hidden", "true");
  }

  showLimitInfo() {
    const limitInfoElement = this.querySelector(".limit_alert");
    if (!limitInfoElement) {
      // console.log("Cannot find limit-info element! Might not be rendered yet...");
      return;
    }
    limitInfoElement.classList.remove("limit_alert_hide");
    limitInfoElement.classList.add("limit_alert_show");
    limitInfoElement.removeAttribute("aria-hidden");
  }

  hideSoldOutAlert() {
    const soldOutAlertElement = this.querySelector(".sold_out_cart_alert");
    if (!soldOutAlertElement) {
      // console.log("Cannot find sold-out-alert element! Might not be rendered yet...");
      return;
    }
    soldOutAlertElement.classList.remove("sold_out_cart_alert_show");
    soldOutAlertElement.classList.add("sold_out_cart_alert_hide");
    soldOutAlertElement.setAttribute("aria-hidden", "true");
  }

  showSoldOutAlert() {
    const soldOutAlertElement = this.querySelector(".sold_out_cart_alert");
    if (!soldOutAlertElement) {
      // console.log("Cannot find sold-out-alert element! Might not be rendered yet...");
      return;
    }
    soldOutAlertElement.classList.remove("sold_out_cart_alert_hide");
    soldOutAlertElement.classList.add("sold_out_cart_alert_show");
    soldOutAlertElement.removeAttribute("aria-hidden");
  }

  sendUpdateItemsMessage(sourceLineItem, cart) {
    // TO DO convert to messenger
    window.postMessage(
      {
        type: "updateCartItems",
        sourceLineItem,
        cart,
      },
      "*"
    );
  }

  /**
   * @param {Cart} cart
   */
  updateItemElement(cart) {
    this.hideSoldOutAlert();
    this.updateTierDiscountMessage(cart, this.tierDiscount);
    this.updatePrices(cart);
  }

  // tierDiscountMessageHtml(originalPrice, finalPrice) {
  //   if (originalPrice === finalPrice) {
  //     return '';
  //   }

  //   const discountPercent = this.calculateDiscountPercent(
  //     originalPrice,
  //     finalPrice
  //   );

  //   const Html = `<div class="tier-discount-message" style="display: none;"><strong>Product Quantity Discount</strong>  |  ${discountPercent}% Applied</div>`;
  //   return Html;
  // }

  calculateDiscountPercent(originalPrice, newPrice) {
    // Is never called and will be replaced by method in calculateDiscountPercent in knocking-library
    const discountPercent = (originalPrice - newPrice) / (originalPrice / 100);
    const discountRounded = Math.round(discountPercent);
    return discountRounded;
  }

  reassembleTierDiscountArray(tierDiscount) {
    if (!tierDiscount) {
      return [];
    }
    let tierDiscountClean;
    if (tierDiscount.substr(tierDiscount.length - 1) === ",") {
      tierDiscountClean = tierDiscount.slice(0, -1);
    }

    const tierDiscountArray = tierDiscountClean.split(",");
    const outputArray = [];
    for (const discount of tierDiscountArray) {
      const discountArray = discount.split(":");
      outputArray.push({
        items: parseInt(discountArray[0]),
        discount: parseInt(discountArray[1]),
      });
    }

    return outputArray;
  }

  /**
   * @param { Cart } cart
   */
  updatePrices(cart) {
    if (!this.item || !this.item.id) {
      console.error("Cannot determine item ID!");
      return;
    }
    const item = cart.items.find((item) => item.id === this.item.id);

    if (!item) {
      return;
    }

    const priceStrikethrough = this.querySelector(".ajaxcart__strikethrough");
    const finalPrice = this.querySelector(".ajaxcart__price");

    if (
      priceStrikethrough &&
      item &&
      item.final_price !== item.original_price
    ) {
      /** @type {HTMLElement | null} */
      const strikeElement = priceStrikethrough.querySelector("strike");
      if (strikeElement) {
        strikeElement.textContent = knocking.formatToDollarCurrencyString(
          item.original_price.toString()
        );
      }
    } else if (priceStrikethrough) {
      /** @type {HTMLElement | null} */
      const strikeElement = priceStrikethrough.querySelector("strike");
      if (strikeElement) {
        strikeElement.textContent = "";
      }
    }

    if (finalPrice) {
      finalPrice.textContent = knocking.formatToDollarCurrencyString(
        item.final_price.toString()
      );
    }
  }

  updateTierDiscountMessage(cart, tierDiscount) {
    let productsInCart = 0;
    for (const item of cart.items) {
      if (item.product_id === this.item.productId) {
        productsInCart += item.quantity;
      }
    }
    const tierDiscountArray = this.reassembleTierDiscountArray(tierDiscount);
    /**
     * @type {HTMLElement | null} tierDiscountMessage
     */
    const tierDiscountMessage = this.querySelector(".tier-discount-message");
    let applicableDiscount = null;
    for (const discount of tierDiscountArray) {
      if (productsInCart >= discount.items) {
        applicableDiscount = discount.discount;
      }
    }
    if (!tierDiscountMessage) {
      return;
    }

    if (applicableDiscount) {
      tierDiscountMessage.querySelector(
        ".tier-discount-percent-output"
      ).textContent = applicableDiscount;
      tierDiscountMessage.style.display = "block";
    } else {
      tierDiscountMessage.style.display = "none";
    }
  }

  render(template, metaArr = [], showTierDiscountMessage = false) {
    // console.log("rendering ajax-cart-item");
    const element = document.createElement("div");
    element.innerHTML = template;

    const shipsByWrapper = element.querySelector(".ships-by-wrapper");
    if (!shipsByWrapper) {
      console.log("Could not find ships by wrapper...");
      return;
    }
    let shipsBy = metaArr.find((meta) => meta.includes("Ships by"));
    let shipsByTrimmed = shipsBy ? shipsBy.replace("Ships by: ", "") : null;
    const shipsByAbsolute =
      knocking.calculateAbsoluteShippingDate(shipsByTrimmed);

    if (!shipsByTrimmed) {
      console.log(
        "Could not create trimmed ships by, might not be rendered yet..."
      );
    }

    if (shipsByAbsolute && !this.item.isVoucherProduct) {
      shipsByWrapper.innerHTML = `
        <div class="ships-by">Ships by<br/><strong>${shipsByAbsolute}</strong></div>
      `;
    }

    this.appendChild(element);
    const nameWrapper = this.querySelector("#ajaxcart-item-name-wrapper");
    if (!nameWrapper) {
      console.error("Could not find name wrapper!");
      return;
    }

    for (let i = 0; i < metaArr.length; i++) {
      const meta = metaArr[i];
      // console.log('meta: ', meta);
      if (meta.includes("Ships by")) {
        continue;
      }
      const metaElement = document.createElement("span");
      metaElement.classList.add("ajaxcart__product-meta");
      metaElement.textContent = meta;
      nameWrapper.appendChild(metaElement);
    }
  }

  getItemMeta() {
    let metaArr = [];
    const data = this.dataset.item ? JSON.parse(this.dataset.item) : {};
    const optionsWithValues = data.options_with_values
      ? data.options_with_values
      : null;
    const properties = data.properties ? data.properties : null;

    if (optionsWithValues) {
      // console.log("optionsWithValues: ", optionsWithValues);
      for (let i = 0; i < optionsWithValues.length; i++) {
        const option = optionsWithValues[i];
        if (!option.value.includes("Default Title")) {
          metaArr.push(`${option.name}: ${option.value}`);
        }
      }
    }

    if (properties) {
      for (const [key, value] of Object.entries(properties)) {
        if (key.charAt(0) !== "_") {
          if (value) {
            metaArr.push(`${key}: ${value}`);
          } else {
            metaArr.push(`${key}`);
          }
        }
      }
    }

    if (this.item && this.item.shipsBy) {
      const shipsByString = this.item.shipsBy;
      // const calculatedShipsBy =
      //   knocking.calculateAbsoluteShippingDate(shipsByString);
      metaArr.push(`Ships by: ${shipsByString}`);
    }

    return metaArr;
  }
}

customElements.define("ajax-cart-item", AjaxCartItem);
